/* import __COLOCATED_TEMPLATE__ from './emoji-reaction.hbs'; */
/* RESPONSIBLE TEAM: team-omni */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
// @ts-ignore
import intermoji from '@intercom/intermoji';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import type { Reaction } from 'embercom/objects/inbox/reaction';
import type EmojiService from 'embercom/services/emoji-service';
import type IntlService from 'ember-intl/services/intl';
import type Session from 'embercom/services/session';

const MAX_DISPLAYED_NAMES = 5;

interface Args {
  reaction: Reaction;
  isUserPart: boolean;
  isUnsupportedReaction?: boolean;
}

interface Signature {
  Args: Args;
}

export default class EmojiReaction extends Component<Signature> {
  @service declare emojiService: EmojiService;
  @service declare session: Session;
  @service intl!: IntlService;

  get displayEmoji() {
    // Convert emoji code to unicode
    let unicodeEmoji = intermoji.unicodeFromIdentifier(this.args.reaction.emoji);

    if (!unicodeEmoji) {
      return `:${this.args.reaction.emoji}:`;
    }

    // Apply skin tone if applicable
    let skinTonedEmoji = this.emojiService.skinTonedModifiedEmoji(unicodeEmoji);

    // Check if browser supports native emoji rendering
    if (intermoji.hasNativeSupport(document)) {
      return skinTonedEmoji;
    } else {
      // Fallback to image rendering
      return sanitizeHtml(intermoji.fallbackImage(16, skinTonedEmoji, 'emoji__image'));
    }
  }

  get count() {
    return this.args.reaction.count > 99 ? '99+' : this.args.reaction.count.toString();
  }

  get reactors() {
    return this.args.reaction.reactors;
  }

  get tooltipContent() {
    let emoji = `:${this.args.reaction.emoji}:`;
    let adminId = this.session.teammate.id.toString();
    let reactors = this.args.reaction.reactors;
    let count = this.args.reaction.count;

    let currentAdminReacted = reactors.some(
      (r) => r.reactor_type === 'admin' && r.reactor_id === adminId,
    );

    let otherNames = reactors
      .filter((r) => !(r.reactor_type === 'admin' && r.reactor_id === adminId))
      .map((r) => r.reactor_name)
      .filter((name): name is string => !!name);

    let othersCount = currentAdminReacted ? count - 1 : count;

    // No names available — legacy count-based fallback
    if (otherNames.length === 0) {
      if (currentAdminReacted && othersCount === 0) {
        return this.intl.t('inbox.conversation-stream.emojis.reaction-you-only', { emoji });
      }
      if (currentAdminReacted && othersCount === 1) {
        return this.intl.t('inbox.conversation-stream.emojis.reaction-you-and-one-unnamed', {
          emoji,
        });
      }
      if (currentAdminReacted) {
        return this.intl.t('inbox.conversation-stream.emojis.reaction-you-and-unnamed', {
          count: othersCount,
          emoji,
        });
      }
      return this.intl.t('inbox.conversation-stream.emojis.reaction', {
        count: this.count,
        emoji,
      });
    }

    // Cap displayed names — extra named reactors count as "others"
    let displayNames = otherNames.slice(0, MAX_DISPLAYED_NAMES);
    let remainingCount = Math.max(0, othersCount - displayNames.length);

    // Current admin reacted + names available
    if (currentAdminReacted) {
      if (remainingCount === 0) {
        if (displayNames.length === 1) {
          return this.intl.t('inbox.conversation-stream.emojis.reaction-you-and-one-named', {
            name: displayNames[0],
            emoji,
          });
        }
        let names = displayNames.slice(0, -1).join(', ');
        let lastName = displayNames[displayNames.length - 1];
        return this.intl.t('inbox.conversation-stream.emojis.reaction-you-and-named', {
          names,
          lastName,
          emoji,
        });
      }
      let names = displayNames.join(', ');
      if (remainingCount === 1) {
        return this.intl.t('inbox.conversation-stream.emojis.reaction-you-named-and-one-unnamed', {
          names,
          emoji,
        });
      }
      return this.intl.t('inbox.conversation-stream.emojis.reaction-you-named-and-unnamed', {
        names,
        count: remainingCount,
        emoji,
      });
    }

    // No current admin — names available
    if (remainingCount === 0) {
      if (displayNames.length === 1) {
        return this.intl.t('inbox.conversation-stream.emojis.reaction-named', {
          name: displayNames[0],
          emoji,
        });
      }
      if (displayNames.length === 2) {
        return this.intl.t('inbox.conversation-stream.emojis.reaction-named-two', {
          firstName: displayNames[0],
          secondName: displayNames[1],
          emoji,
        });
      }
      let names = displayNames.slice(0, -1).join(', ');
      let lastName = displayNames[displayNames.length - 1];
      return this.intl.t('inbox.conversation-stream.emojis.reaction-named-list', {
        names,
        lastName,
        emoji,
      });
    }

    let names = displayNames.length === 1 ? displayNames[0] : displayNames.join(', ');
    if (remainingCount === 1) {
      return this.intl.t('inbox.conversation-stream.emojis.reaction-named-and-one-unnamed', {
        names,
        emoji,
      });
    }
    return this.intl.t('inbox.conversation-stream.emojis.reaction-named-and-unnamed', {
      names,
      count: remainingCount,
      emoji,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    EmojiReaction: typeof EmojiReaction;
    'emoji-reaction': typeof EmojiReaction;
  }
}
